<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { MovesQuality, type Move, type StatisticType } from '@/types'
import ActionLabel from './ActionLabel.vue'
import DoughnutChart from '@/components/statistics/DoughnutChart.vue'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group/'
import { actionIcons } from '@/constants'
import Typography from '../ui/typography/Typography.vue'

const props = defineProps<{ moves: Move[]; currentSlide: number; statisticType: StatisticType }>()
const totalBtn = ref()
const toggleRef = ref(props.statisticType)
const total = computed(() => props.moves.reduce((sum, move) => sum + Number(move.count), 0))
const emit = defineEmits(['update-statistic-type'])

const actionValues = computed(() => {
  const values = props.moves?.reduce(
    (acc, move) => {
      acc[move.quality] = move.count
      return acc
    },
    {} as Record<keyof typeof MovesQuality, number>
  )
  return values
})

watch(
  () => toggleRef.value,
  () => {
    emit('update-statistic-type', toggleRef.value)
  }
)
</script>

<template>
  <div class="flex flex-col justify-center items-center bg-dark-grey w-full rounded-lg p-3.5 mb-3.5">
    <div class="flex flex-row w-full justify-between mb-3.5">
      <Typography size="text-16-24" class="font-semibold">{{
        $t('Statistic.label.Analyse')
      }}</Typography>
      <ToggleGroup v-model="toggleRef" type="single">
        <ToggleGroupItem value="session" variant="rounded">
          {{ $t('Statistic.button.Session') }}
        </ToggleGroupItem>
        <ToggleGroupItem value="total" variant="rounded" ref="totalBtn">
          {{ $t('Statistic.button.Total') }}
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
    <DoughnutChart v-if="actionValues" class="mb-3.5" :total="total" :actions="actionValues" />
    <ActionLabel
      v-for="move in moves"
      :key="move.quality"
      :label="MovesQuality[move.quality]"
      :icon="actionIcons[move.quality]"
      :count="Number(move.count)"
    />
  </div>
</template>
